import * as z from 'zod';

// The Menu screen is fed by one catalog payload: the sidebar taxonomies
// (promotions, categories) plus a flat product list grouped by category
// at render time.

/**
 * Whether the branch is currently selling the product. Mirrors the wire's
 * `v1ProductStatus` minus its protobuf spelling: `PRODUCT_STATUS_STOP_LIST`
 * means the kitchen has taken it off the menu ("стоп-лист"). An unset/unknown
 * wire value maps to `available`, matching how the catalog reads today.
 */
export const PRODUCT_AVAILABILITIES = ['available', 'stopList'] as const;
export const productAvailabilitySchema = z.enum(PRODUCT_AVAILABILITIES);
export type ProductAvailability = z.infer<typeof productAvailabilitySchema>;

/**
 * Where a product stands in the MODERATION lifecycle — the state the catalog's
 * filter pills and card badge read. Distinct from {@link ProductAvailability},
 * which is the branch's stop-list switch: a product can be `life` (published)
 * and stop-listed at the same time.
 *
 * Mirrors the wire's `moderation_state` enum:
 * - `pending` — «В модерации», handed in and waiting (`ready_for_life`).
 * - `approved` — «Прошел модерацию», live on the storefront (`life`).
 * - `rejected` — «Отклонено», moderation sent it back.
 * - `draft` — «created», waiting for the merchant to send it to moderation.
 */
export const PRODUCT_MODERATIONS = ['draft', 'pending', 'approved', 'rejected'] as const;
export const productModerationSchema = z.enum(PRODUCT_MODERATIONS);
export type ProductModeration = z.infer<typeof productModerationSchema>;

/**
 * The states the catalog offers as filters, in the design's pill order.
 */
export const PRODUCT_MODERATION_FILTERS = [
  'draft',
  'pending',
  'approved',
  'rejected',
] as const;
export type ProductModerationFilter = (typeof PRODUCT_MODERATION_FILTERS)[number];

/** Display unit from the wire's `measurement_type`; absent for per-item goods. */
export const MEASUREMENT_TYPES = [
  'MEASUREMENT_TYPE_GR',
  'MEASUREMENT_TYPE_KG',
  'MEASUREMENT_TYPE_ML',
  'MEASUREMENT_TYPE_L',
  'MEASUREMENT_TYPE_PCS',
  'MEASUREMENT_TYPE_PORTION',
] as const;
export const measurementTypeSchema = z.enum(MEASUREMENT_TYPES);
export type MeasurementType = z.infer<typeof measurementTypeSchema>;

/**
 * One field's translations. `ru` is canonical — the storefront falls back to it
 * for any locale with no translation — so it is the only required member. An
 * empty `uz`/`en` means "not translated yet", never "translated to empty".
 */
export const localizedFieldSchema = z.object({
  ru: z.string(),
  uz: z.string().optional(),
  en: z.string().optional(),
});
export type LocalizedField = z.infer<typeof localizedFieldSchema>;

/**
 * The two SHAPES a modifier group can wear in the editor, derived from
 * `maxSelect`: exactly-one (a radio group) or several (checkboxes). Derived,
 * not stored — the wire regained the independent `min_select`/`max_select`
 * pair in API v1.00.227, and `selection_type` is deleted; see
 * `menuModifierGroupSchema`.
 */
export const MODIFIER_SELECTIONS = ['single', 'multi'] as const;
export const menuModifierSelectionSchema = z.enum(MODIFIER_SELECTIONS);
export type MenuModifierSelection = z.infer<typeof menuModifierSelectionSchema>;

/** `maxSelect === 1` is the old SINGLE (radio); anything else is multi. */
export function toModifierSelection(
  maxSelect: number | undefined,
): MenuModifierSelection {
  return maxSelect === 1 ? 'single' : 'multi';
}

/**
 * One selectable choice inside a modifier group — "Маленькая, 25 см", "+8 000".
 *
 * `priceDelta` is what the option ADDS to the product's price in so'm, so 0 is
 * a real and common value (the baseline size costs nothing extra), not "unset".
 */
export const menuModifierOptionSchema = z.object({
  /** Server-assigned; absent for an option the merchant just added. */
  id: z.string().optional(),
  /**
   * The option's name in every language. Localized as of API v1.00.180 —
   * `v1LocalizedModifierOption.name` is a `v1LocalizedText`, so Uz/En are now
   * persisted rather than dropped on save.
   */
  name: localizedFieldSchema,
  /** So'm ADDED to the product's price (`price` on the wire, was `price_delta`). */
  priceDelta: z.number().int().nonnegative(),
  /** OFD fiscal classifier code for this option (v1.00.227+). */
  ikpuCode: z.string().optional(),
  /** Soliq measurement unit code (e.g. '5' for pcs). */
  unitCode: z.string().optional(),
  /** VAT %; absent = inherits, `0` = no VAT. */
  vatPercent: z.number().optional(),
});
export type MenuModifierOption = z.infer<typeof menuModifierOptionSchema>;

/**
 * A group of related choices attached to a product — "Размер пиццы", "Тип теста".
 *
 * As of API v1.00.180 both the group's and its options' names are LOCALIZED
 * (`v1LocalizedText`), so all three languages round-trip — the Ru-only
 * narrowing this editor used to do is gone.
 */
export const menuModifierGroupSchema = z.object({
  /** Server-assigned; absent for a group the merchant just added. */
  id: z.string().optional(),
  /** Every language — see `menuModifierOptionSchema.name`. */
  name: localizedFieldSchema,
  /**
   * How many options the customer MUST pick (`min_select`). `0` makes the
   * group skippable. Back on the wire as of API v1.00.227, which deleted the
   * `selection_type` enum of v1.00.180 and restored the independent pair —
   * "required, pick up to three" is expressible again, so the design's
   * «Обязательно» toggle finally has a field to write to.
   */
  minSelect: z.number().int().nonnegative(),
  /**
   * How many the customer MAY pick (`max_select`). ABSENT means no ceiling;
   * `1` is the old SINGLE (a radio group). Kept as the full number, not
   * collapsed to single/multi, so a provider-imported range like min=max=8
   * round-trips instead of being flattened by an unrelated edit.
   */
  maxSelect: z.number().int().positive().optional(),
  options: z.array(menuModifierOptionSchema),
});
export type MenuModifierGroup = z.infer<typeof menuModifierGroupSchema>;

export const menuProductSchema = z.object({
  id: z.union([z.string(), z.number()]),
  categoryId: z.union([z.string(), z.number()]),
  /**
   * The catalog's display name — already resolved to the active locale by the
   * backend. The editor needs every translation instead, which the read
   * endpoints don't carry, so `translations` is populated only after a write
   * round-trips (see `nameTranslations`).
   */
  name: z.string(),
  description: z.string().optional(),
  /**
   * All translations of `name`/`description`, when known. The list endpoints
   * return a locale-resolved string only, so this is absent for a product read
   * from the catalog and present for one just created/updated — the editor
   * seeds its Uz/En inputs from it and falls back to the resolved `name`.
   */
  nameTranslations: localizedFieldSchema.optional(),
  descriptionTranslations: localizedFieldSchema.optional(),
  compositionTranslations: localizedFieldSchema.optional(),
  nutrition: z
    .object({
      calories: z.number().int().nullish(),
      fats: z.number().nullish(),
      proteins: z.number().nullish(),
    })
    .nullish(),
  /** Branches this product is stocked in (per-branch overlays on the wire). */
  branchIds: z.array(z.string()).optional(),
  /** Current price in so'm (integer — so'm has no minor units). */
  price: z.number().nonnegative(),
  /** Branch-local stock count when a product read is scoped to a restaurant. */
  stockQuantity: z.number().int().nonnegative().optional(),
  /** Pre-discount price; present only for discounted products (struck through). */
  originalPrice: z.number().nonnegative().optional(),
  discountPercent: z.number().int().min(0).max(100).optional(),
  /**
   * The cover photo as an absolute URL, for `<img src>`. The backend expands
   * the stored object KEY into this on read, so it is a RENDERING of the photo,
   * not its identity — the write endpoints take the key instead, and the read
   * shapes carry no way back to it. A save therefore must never send this
   * value: an unchanged photo is left alone (the PATCH omits `images`), and
   * only a freshly-picked file produces a key to persist.
   */
  imageUrl: z.string().optional(),
  /**
   * The full photo gallery, as absolute URLs. Same caveat as `imageUrl` — these
   * are expansions of the stored keys, not the keys themselves, so they seed
   * the editor's gallery for display but are never written back.
   */
  imageUrls: z.array(z.string()).optional(),
  /**
   * The gallery's server-side image IDS, positionally aligned with
   * `imageUrls`. An id is the only handle a client has on a stored photo — the
   * URL cannot be written back — so this is what lets the editor say "keep
   * these four, delete that one" instead of replacing the whole gallery.
   */
  imageIds: z.array(z.string()).optional(),
  /**
   * Index into `imageUrls`/`imageIds` of the COVER photo (the wire's `image_type`).
   *
   * Carried explicitly because `image_type` and `position` are independent: a
   * merchant can star the third photo without reordering the gallery, so the
   * cover is not simply the first entry. Without this the editor showed the
   * wrong photo as cover and then re-asserted that wrong choice on save.
   */
  coverIndex: z.number().int().nonnegative().optional(),
  /**
   * Sale state; `stopList` products render dimmed with a badge. Optional in the
   * type (absent means sellable) so callers building a product for an optimistic
   * cache patch don't have to restate the default.
   */
  availability: productAvailabilitySchema.optional(),
  /**
   * Moderation state (the wire's `moderation_state`). Optional because the
   * older read shapes carry no such field and callers building a product for
   * an optimistic cache patch don't restate it — absent simply means "not
   * known", which the catalog renders as no badge rather than as a draft.
   */
  moderation: productModerationSchema.optional(),
  /** Why moderation sent the product back (`rejection_reason`). */
  rejectionReason: z.string().optional(),
  /** Customer-facing display unit from `measurement_type`. */
  measurementType: measurementTypeSchema.optional(),
  /**
   * The kitchen keeps this product on hand, so an order needs no cooking wait.
   * Mutually exclusive with `cookTimeSeconds`: when true the prep time is not
   * just unset but meaningless, and the form hides/clears it.
   */
  alwaysReady: z.boolean().optional(),
  /** 18+ product: its card masks the photo with the age-restriction stamp. */
  isAdult: z.boolean().optional(),
  /**
   * The dish's modifiers are switched off wholesale (every branch); its groups
   * are kept and come back when the switch is turned off. On a WRITE,
   * `undefined` means "don't touch" — the switch has its own endpoint, so a
   * save that did not change it sends nothing.
   */
  modifiersDisabled: z.boolean().optional(),
  /**
   * Preparation time in SECONDS (the wire unit — `cook_time_seconds`). The form
   * collects whole minutes and converts, because merchants think in minutes.
   * Absent when the product is `alwaysReady`.
   */
  cookTimeSeconds: z.number().int().nonnegative().optional(),
  /**
   * IKPU/SPIC classifier code — the tax classifier the OFD (fiscal receipt)
   * integration requires. Merchant-only: the public catalog never carries it.
   */
  ikpuCode: z.string().optional(),
  /**
   * The product falls in a category subject to mandatory goods marking, so a
   * Data Matrix code must accompany it on a receipt. Gates `markedCode` — the
   * editor only asks for one when this is true.
   */
  hasMarkedCode: z.boolean().optional(),
  /** Marking (Data Matrix) code; meaningful only when `hasMarkedCode`. */
  markedCode: z.string().optional(),
  /** OFD measurement-unit classifier code (Soliq tasnif). */
  unitCode: z.string().optional(),
  /** VAT %; absent = the brand's rate, `0` = explicitly no VAT. */
  vatPercent: z.number().optional(),
  /** Net amount of one unit, in the unit `unitCode` names (`measure_amount`). */
  measureAmount: z.number().positive().optional(),
  /**
   * Selling hours set on the dish ("08:00"–"11:00"). Absent on a side = no
   * limit there; absent on both = sold all day.
   */
  availableFrom: z.string().optional(),
  availableTo: z.string().optional(),
  /**
   * The BRANCH's own hours, on branch-scoped reads. Absent = the branch set
   * none and inherits the dish's, exactly like `own_price` — so an editor can
   * tell an override from an inherited value.
   */
  ownAvailableFrom: z.string().optional(),
  ownAvailableTo: z.string().optional(),
  /**
   * The product's modifier groups (sizes, dough types, add-ons).
   *
   * OPTIONAL, and the distinction from `[]` is load-bearing on write: the
   * update body reads an absent `modifiers` as "don't touch" and a present one
   * — even empty — as "replace/clear" (the wire's own words on
   * `v1ModifierSchema`). A product read that carries no groups yields `[]`;
   * an editor that never visited the modifier step leaves this undefined so the
   * PATCH omits it rather than wiping groups the merchant never saw.
   */
  modifiers: z.array(menuModifierGroupSchema).optional(),
});
export type MenuProduct = z.infer<typeof menuProductSchema>;

export const menuCategorySchema = z.object({
  id: z.union([z.string(), z.number()]),
  sortOrder: z.number().int().min(1).optional(),
  createdAt: z.string().optional(),
  /** Display name, already resolved to the active locale by the backend. */
  name: z.string(),
  /**
   * All translations of `name`, when known. Every category READ endpoint
   * answers with the locale-resolved string only, so this is absent for a
   * category read from the catalog and present for one just created/renamed —
   * the rename form seeds its Uz/En inputs from it and falls back to the
   * resolved `name` for Ru. Mirrors `MenuProduct.nameTranslations`.
   */
  nameTranslations: localizedFieldSchema.optional(),
  /**
   * Moderation state, shown as the coloured status glyph beside the row in the
   * sidebar and narrowed by the sidebar's status select.
   *
   * Reuses the PRODUCT vocabulary rather than defining a parallel one: the two
   * are the same review lifecycle over different entities, and the design
   * badges them with the same three states («В модерации» / «Активна» /
   * «Отклонена»).
   *
   * OPTIONAL, and in practice absent today: `v1ProductCategory` carries no
   * lifecycle field, so `toMenuCategory` has nothing to read. Modelled anyway
   * so the row badges the moment the backend grows one — and left undefined
   * rather than defaulted, because badging every category «Активна» would
   * assert a review outcome none of them received.
   */
  moderation: productModerationSchema.optional(),
  /** Why moderation sent the category back (`rejection_reason`). */
  rejectionReason: z.string().optional(),
});
export type MenuCategory = z.infer<typeof menuCategorySchema>;

export const menuPromotionSchema = z.object({
  id: z.union([z.string(), z.number()]),
  name: z.string(),
});
export type MenuPromotion = z.infer<typeof menuPromotionSchema>;

export const menuSchema = z.object({
  promotions: z.array(menuPromotionSchema),
  categories: z.array(menuCategorySchema),
  products: z.array(menuProductSchema),
});
export type Menu = z.infer<typeof menuSchema>;
